import { Component, Injector } from '@angular/core';
import type { OnInit } from '@angular/core';
import type { ModalOptions } from '@ionic/angular';
import { NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import {
    DealershipService, IsoCountriesService, ZipcodesService, AddressVerificationService } from '@shared/services';
import { AddressBasePage } from '@shared/pages';
import { RadioModalPage } from '@shared/pages';
// import { GenerateDataService } from '@shared/services';
import { Constants } from '@shared/models';
import type { TDealership, TRegionDocument } from '@shared/models';
import type { TIsoCountry, TCreateAccountInfo, TRegion, TResponse } from '@shared/models';
import { PhoneMaskDirective } from '@shared/directives';

@Component({
    'selector': 'app-dealership',
    'templateUrl': './dealership.page.html',
    'styleUrls': ['./dealership.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule, PhoneMaskDirective ]
})
export class DealershipPage extends AddressBasePage implements OnInit {
    public debugMode: boolean;
    private isoCountries: TIsoCountry[];
    private defaultCountry: TIsoCountry;
    public dealership: TDealership;
    public error = {};
    public saveAttempt = false;
    public submitted = false;
    public addressVerified = false;
    private pageLoading: boolean;
    private regionDoc: TRegionDocument;
    private countryName: string;

    // private generateDataService: GenerateDataService,
    constructor(private dealershipService: DealershipService,
        private zipcodesService: ZipcodesService,
        private addressVerificationService: AddressVerificationService,
        private isoCountriesService: IsoCountriesService, 
        injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(Constants.VIEW_DEALERSHIP);
    }

    // private foo() {
    //     // type Activity = typeof TDealership;
    //     const headers: Array<Object> = Object.keys(TDealership).map((key) => {
    //         return { text: key, value: key };
    //     });
    // }

    public override async ngOnInit(): Promise<void> {
        this.debugMode = this.sessionService.isDebugMode();

        this.pageLoading = true;
        // Use placeholder values so template bindings remain safe while Firestore is loading.
        this.dealership = this.dealershipService.getNullable();
        super.ngOnInit();

        try {
            await this.uxHelperService.showSpinner();
            const countries = await this.isoCountriesService.getAll();
            this.isoCountries = this.isoCountriesService.sortCountries(countries);
            this.defaultCountry = await this.isoCountriesService.getByCountryName('United States of America');

            const loadedDealership = await this.dealershipService.get();
            if (loadedDealership !== null) {
                this.dealership = loadedDealership;
            }

            this.dealership.phoneNumber = this.utilsService.formatPhoneNumberForDisplay(this.dealership?.phoneNumber);
            this.dealership.address.isoCountryCode = this.defaultCountry.isoCountryCode;
            this.regionDoc = this.zipcodesService.getRegionDocument();

            this.initMoreMenu();
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
            this.pageLoading = false;
        }
    }

    public initMoreMenu(): void {
        this.resetPopoverMenu();
        this.pushPopoverMenuItem('Show Tour', 'show-dealership-tour');
    }

    public async onStateClick() {
        const stateNames = [];
        const statesInfo = Constants.US_STATES;
        statesInfo.forEach((info) => {
            stateNames.push(info.regionName);
        });

        const response: TResponse = await this.uxHelperService.showRadioAlert2(
            'Select State',
            stateNames,
            this.dealership.address.regionName,
            RadioModalPage
        );
        if (response.okay) {
            this.dealership.address.regionName = Array.isArray(response.selectedItems)
                ? response.selectedItems[0] ?? ''
                : response.selectedItems
            const regionInfo = this.getRegionInfo(this.dealership.address.regionName);
            this.dealership.address.regionCode = regionInfo?.regionCode ?? '';
        }
    }

    private getRegionInfo(regionName: string): TRegion | undefined {
        const region = Constants.US_STATES.find((info) => info.regionName === regionName);
        if (!region) {
            const msg = 'State / Region ' + regionName + ' not found.';
            this.uxHelperService.showAlert('Error', msg);
            return undefined;
        }
        return region;
    }

    /*
    public async onCountryClick() {
        const items = this.isoCountries.map(o => o.countryName);

        const options: ModalOptions = {
            component: RadioModalPage,
            componentProps: {
                'items': items,
                'selectedItem': this.countryName,
                'title': 'Select Country'
            },
            backdropDismiss: false,
            showBackdrop: true,
            cssClass: 'myModalSize'
        };

        const myModal = await this.modalController.create(options);
        await myModal.present();
        const response: any = await myModal.onDidDismiss();
        if (response.data.okay) {
            const selectedCountryName = response.data.selectedItem;
            const country = this.isoCountries.find(o => o.countryName === selectedCountryName);

            this.dealership.address.countryName = country.countryName;
            this.dealership.address.isoCountryCode = country.isoCountryCode;
        }
    }
    */

    // private loadCountry(countryName: string) {
    //     const country = this.isoCountries.find(o => o.countryName === countryName);
    //     // this.vm.countryName = country.countryName;
    //     // this.vm.countryAbbr = country.countryAbbr;
    //     // this.vm.countryCode = country.isoCountryCode;
    //     // this.vm.phonePrefixes = country.phonePrefixes;
    //     // this.regions = country.regions;
    //     return country;
    // }

    public async onAddressChange() {
        this.addressVerified = false;
    }

    public async onZipcodeChange() {
        // While the page is loading, do not process the body of this method.
        if (this.pageLoading) {
            return;
        }

        if (this.dealership?.address.zipcode?.length === 5) {
            try {
                await this.uxHelperService.showSpinner('Loading data.');

                // const {regionInfo, zipInfo} = await this.zipcodesService.getRegionAndZipcodeInfo(
                // const info: TRegionZipcodeInfo = await this.zipcodesService.getRegionAndZipcodeInfo(
                //     this.dealership.address.regionCode, this.dealership.address.zipcode);

                if (this.regionDoc !== undefined) {
                    const zipInfo = this.zipcodesService.getZipcodeInfo(this.dealership.address.zipcode);

                    // If the zipcode that was just entered is in the state then okay to proceed;
                    // otherwise, it is an invalid zipcode.
                    if (zipInfo) {
                        if (this.regionDoc.statecode === this.dealership.address.regionCode) {
                            this.dealership.address.regionName = this.regionDoc.state;
                            this.dealership.address.city = zipInfo.city;
                        }
                    } else {
                        const msg = 'Zipcode ' + this.dealership.address.zipcode + ' is not in ' +
                            this.dealership.address.regionName + '.';
                        await this.uxHelperService.showToast(msg);
                    }


                    // // If the zipcode that was just entered is in the state then okay to proceed;
                    // // otherwise, it is an invalid zipcode.
                    // if (!zipInfo) {
                    //     const msg = 'Zipcode ' + this.dealership.address.zipcode + ' is not in ' + 
                    //     this.dealership.address.regionName + '.';
                    //     await this.uxHelperService.showToast(msg);
                    // } else {
                    //     if (this.regionDoc.statecode === this.dealership.address.regionCode) {
                    //         this.dealership.address.regionName = this.regionDoc.state;
                    //         this.dealership.address.city = zipInfo.city;
                    //     }
                    // }
                }

                // if (regionInfo && zipInfo && regionInfo.statecode === this.dealership.address.regionCode) {
                //     this.dealership.address.regionName = regionInfo.state;
                //     this.dealership.address.city = zipInfo.city;
                // } else {
                //     const msg = 'Zipcode ' + this.dealership.address.zipcode + ' is not in ' + 
                //     this.dealership.address.regionName + '.';
                //     await this.uxHelperService.showToast(msg);
                // }
                // if (info && info. zipInfo && regionInfo.statecode === this.dealership.address.regionCode) {
            } catch (err) {
                await this.uxHelperService.hideSpinner();
                const errorMessage = this.utilsService.extractError(err);
                await this.uxHelperService.showAlert('Error', errorMessage);
            } finally {
                await this.uxHelperService.hideSpinner();
            }
        }
        this.addressVerified = false;
    }

    public async onVerifyAddressClick(form: NgForm) {
        this.submitted = true;
        if (!form.valid) {
            return;
        }
        try {
            this.addressVerified = false;
            await this.uxHelperService.showSpinner('Verifing address.');

            const requestedAddress = this.dealership.address;
            const validation = await this.addressVerificationService.verifyAddress(requestedAddress);

            // await this.uxHelperService.hideSpinner();
            // await await this.uxHelperService.showAlert(validation.statusTitle, validation.statusMessage);


            if (validation.found) {
                this.addressVerified = true;
                this.dealership.address.streetAddress = validation.foundAddress;
                this.dealership.address.fullAddress = validation.location.formattedAddress;
                this.dealership.address.coordinate = validation.location.coordinate;
                this.addressVerified = true;
            }
            await this.uxHelperService.hideSpinner();

            if (!validation.statusTitle.includes('We do not service this zipcode')) {
                await this.potentiallyUseFoundInfo(validation, this.dealership.address);
            }
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Address Not Found', errorMessage);
        }
    }

    public isAddressVerified(): boolean {
        return this.addressVerified;
    }

    public async onSaveClick(form: NgForm) {
        this.submitted = true;
        if (!form.valid) {
            return;
        }

        try {
            await this.uxHelperService.showSpinner('Saving...');

            // If the user typed in "(661) 877-8631"  Discard ()- and a space
            // So only the digits are kept.  
            this.dealership.phoneNumber.replace(/\D/g, '');
            this.dealership.phoneNumber = this.utilsService.unformatPhoneNumber(this.dealership.phoneNumber);
            this.dealership.address.countryAbbr = this.defaultCountry.countryAbbr;

            await this.dealershipService.set(this.dealership);
            this.sessionService.setDealership(this.dealership);

            // const ret: any = await this.generateDataService.buildDefaultData(this.dealership);
            // if (ret.defaultServiceAreaCreated) {
            //     await this.uxHelperService.showAlert('Info', 'A service area for this zipcode has automatically been created.');
            // }
            this.uxHelperService.showToast('Saved.');
            this.router.navigateByUrl('/managerSettings');
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err, 'Invalid Address');
            await this.uxHelperService.showAlert('Error: ', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    } 
}
